import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Timer } from "lucide-react";

/**
 * Returns the milliseconds until the next UTC midnight (when the daily
 * challenge resets server-side).
 */
function msUntilNextUtcMidnight() {
  const now = new Date();
  const next = new Date(
    Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate() + 1,
      0,
      0,
      0,
      0,
    ),
  );
  return Math.max(0, next.getTime() - now.getTime());
}

function formatRemaining(ms) {
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  if (m > 0) return `${m}m ${String(s).padStart(2, "0")}s`;
  return `${s}s`;
}

/**
 * Inline countdown chip: "Resets in 4h 32m".
 * Variant controls the color scheme: "warm" (amber on dark) or "cool" (ink on white).
 */
export default function DailyCountdown({ variant = "warm", testId = "daily-countdown" }) {
  const { t } = useTranslation();
  const [remaining, setRemaining] = useState(msUntilNextUtcMidnight());

  useEffect(() => {
    const tick = window.setInterval(() => {
      setRemaining(msUntilNextUtcMidnight());
    }, 1000);
    return () => window.clearInterval(tick);
  }, []);

  const classes =
    variant === "warm"
      ? "bg-white/25 dark:bg-slate-900/25 text-white"
      : "bg-amber-100 text-amber-800 border border-amber-200";

  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold ${classes}`}
    >
      <Timer className="w-3 h-3" />
      {t("dailyCountdown.resetsIn", { remaining: formatRemaining(remaining) })}
    </span>
  );
}
